import axios from "axios";
import { Cookies } from "react-cookie";

import { StaticValue } from "../seed/constants";
import {
	clearAuthorizationToken,
	getAuthorizationToken,
	isClaimAuthorizationToken,
	isUserAuthorizationToken,
} from "./auth/authToken";
import { clearClaimSession, getClaimToken } from "./companies/claimSession";

const staticValue = new StaticValue();
const staticValues = staticValue.getDefaultValues();

const cookies = new Cookies();

function applyRequestAuthorization(config) {
	const configuredAuthToken = config.headers?.Authorization;
	const authToken = cookies.get("Authorization");
	const claimToken = getClaimToken();

	if (config.authMode === "claim") {
		delete config.headers.Authorization;
		if (claimToken) config.headers.Authorization = claimToken;
	} else if (configuredAuthToken) {
		// A token supplied directly on this request, or synchronously applied to the axios client,
		// is authoritative. Do not delete it merely because the cookie has not become readable yet.
		config.headers.Authorization = configuredAuthToken;
	} else if (authToken && authToken !== claimToken) {
		config.headers.Authorization = authToken;
	} else {
		delete config.headers.Authorization;
	}

	return config;
}

export const API = axios.create({
	baseURL: staticValues.apiUrl,
	headers: {
		"Content-Type": "application/json",
		...staticValues.commonHeader,
	},
});

API.interceptors.request.use(applyRequestAuthorization);

/**
 * Point both axios layers at a token the moment a provider issues one.
 *
 * Every Authorization default in the app is snapshotted from StaticValue at
 * module load — which happens while the visitor is still signed out. A full page
 * load after sign-in re-runs that snapshot, which is why signing in works. The
 * register flow reaches /onboarding and /profile through client-side navigation
 * instead, so without this the defaults still hold the signed-out empty string
 * and the first authenticated call comes back 401.
 */
export function applyAuthorizationHeader(token) {
	// ResumeDataService configures global axios lazily. Do that before applying the token so its
	// one-time defaults initialization cannot wipe this header on the next client-side route.
	configureLegacyGlobalAxios();

	// The instance was created with the snapshot spread over its top-level
	// headers, so that copy has to be replaced as well as the shared `common`.
	API.defaults.headers.Authorization = token;
	API.defaults.headers.common.Authorization = token;

	// ResumeDataService issues its requests through the global axios instance.
	axios.defaults.headers.common.Authorization = token;
}

/**
 * One shared auth-failure policy for BOTH axios clients (the shared `API`
 * instance and the legacy global `axios` still used by ResumeDataService).
 *
 * - 401: the credential could not be resolved to a user — clear it and send the
 *   visitor to /signin, preserving where they were.
 * - 403: an authenticated permission/ownership failure (or an unsupported PATCH
 *   section). This is NOT an authentication failure — surface it to the caller,
 *   never clear the session. (The backend returns 401, not 403, for
 *   missing/expired credentials.)
 * - 402: payment required — raised by the create / copy / template-change flows.
 *
 * The navigation is guarded so concurrent failures across both clients start at
 * most one redirect.
 */
let authRedirectStarted = false;

function beginAuthRedirect(destination) {
	if (authRedirectStarted) return;
	authRedirectStarted = true;
	window.location.href = destination;
}

export function onAuthFailure(error) {
	const status = error?.response?.status;

	if (status === 402) {
		beginAuthRedirect("/payments");
		return;
	}

	// Only 401 means "not authenticated". 403 is a permission error for an
	// authenticated user and must not log them out.
	if (status !== 401) return;

	if (error.config?.authFailureMeansEmpty) return;

	const ref = encodeURIComponent(window.location.pathname + window.location.search + window.location.hash);

	if (error.config?.authMode === "claim") {
		clearClaimSession();
		beginAuthRedirect(`/signin?ref=${ref}`);
		return;
	}

	// An anonymous author's credential is the id of the throwaway user their
	// resume was created under. A 401 means that resume is gone, not that a
	// session expired — there is no account behind it, so /signin is a form they
	// can never satisfy. Drop the dead pointers instead and leave them where they
	// are: the builder then behaves like a first visit and their next step starts
	// a new resume.
	const token = getAuthorizationToken();
	if (token && !isUserAuthorizationToken(token) && !isClaimAuthorizationToken(token)) {
		clearAuthorizationToken();
		cookies.remove(staticValues.localStorageResumeId, { path: "/" });
		return;
	}

	clearAuthorizationToken();
	localStorage.removeItem("profileData");
	localStorage.removeItem("isDropzOpsUser");
	localStorage.removeItem("resumeList");
	beginAuthRedirect(`/signin?ref=${ref}`);
}

API.interceptors.response.use(
	(response) => response,
	(error) => {
		onAuthFailure(error);
		return Promise.reject(error);
	},
);

/**
 * Configure the legacy global `axios` instance that ResumeDataService still
 * issues through. Idempotent. Mirrors the `API` client: non-auth defaults from
 * commonHeader, Authorization applied per-request from the live cookie (so a
 * prefix-less anonymous resume token is honoured rather than blanked by a stale
 * snapshot), and the shared auth-failure policy. Retire this once the last
 * ResumeDataService caller moves to `API`.
 */
let legacyGlobalAxiosConfigured = false;

export function configureLegacyGlobalAxios() {
	if (legacyGlobalAxiosConfigured) return;
	legacyGlobalAxiosConfigured = true;

	axios.defaults.headers.common = { ...staticValues.commonHeader };

	axios.interceptors.request.use(applyRequestAuthorization);

	axios.interceptors.response.use(null, (error) => {
		onAuthFailure(error);
		return Promise.reject(error);
	});
}
